import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ManageWithdraw from './ManageWithdraw';
import ManageReferral from './ManageReferral';
import ManageUsers from './ManageUsers';
import AdminTicketPage from './AdminTicketPage';

const Dashadmin = () => {
  // State to track which component is currently visible
  const [visibleComponent, setVisibleComponent] = useState(null);

  // State to store all tables' data
  const [data, setData] = useState({
    v1_users: [],
    user_data: [],
    referral_data: [],
  });

  // Fetch data from different tables when the component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse, userDetailsResponse, referralDataResponse] = await Promise.all([
          axios.get('https://www.sharedvision-partners.online/v1/API/Admin/get_v1_users.php'),
          axios.get('https://sharedvision-partners.online/v1/API/Admin/get_user_data.php'),
          axios.get('https://www.sharedvision-partners.online/v1/API/Admin/get_referral_data.php'),
        ]);

        setData({
          v1_users: usersResponse.data?.users || [],
          user_data: userDetailsResponse.data?.user_data || [],
          referral_data: referralDataResponse.data?.referral_data || [],
        });
      } catch (error) {
        console.error('Error fetching data from tables:', error);
      }
    };

    fetchData();
  }, []);

  // Function to handle toggling of components
  const showComponent = (component) => {
    setVisibleComponent(component);
  };

  // Optimistic UI update and backend update
  const updateData = async (table, updatedData) => {
    try {
      // Optimistic UI Update
      const updatedTableData = data[table].map((item) =>
        item.id === updatedData.id ? updatedData : item
      );

      // Update the state with the optimistic update
      setData((prevState) => ({
        ...prevState,
        [table]: updatedTableData,
      }));

      // Send the update to the backend
      const response = await axios.post(
        `https://www.sharedvision-partners.online/v1/API/update_${table}.php`,
        updatedData
      );

      if (!response.data.success) {
        throw new Error(`Error updating ${table}`);
      }
    } catch (error) {
      console.error(`Error updating ${table}:`, error);
      // Rollback the optimistic UI update if backend fails
      setData((prevState) => ({
        ...prevState,
        [table]: prevState[table].map((item) =>
          item.id === updatedData.id ? updatedData : item
        ),
      }));

      alert('An error occurred while updating. Please try again.');
    }
  };

  // Helper function to render buttons for toggling components
  const renderButton = (componentName, label) => (
    <button style={styles.button} onClick={() => showComponent(componentName)}>
      {label}
    </button>
  );

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Admin Settings</h2>
      <p style={styles.subHeading}>Configure your user preferences here.</p>

      {/* Buttons to toggle visibility of different components */}
      <div style={styles.buttonContainer}>
        {renderButton('manageReferral', 'Manage Referrals')}
        {renderButton('manageWithdraw', 'Manage Withdrawals')}
        {renderButton('manageUsers', 'Manage Users')}
        {renderButton('adminTickets', 'Admin Tickets')}

      </div>

      {/* Conditionally render components based on the state */}
      <div style={styles.componentContainer}>
        {visibleComponent === 'manageWithdraw' && (
          <ManageWithdraw
            user_data={data.user_data}
            updateData={(updatedUserData) => updateData('user_data', updatedUserData)}
          />
        )}
        {visibleComponent === 'manageReferral' && (
          <ManageReferral
            referral_data={data.referral_data}
            updateData={(updatedReferralData) =>
              updateData('referral_data', updatedReferralData)
            }
          />
        )}
        {visibleComponent === 'manageUsers' && (
          <ManageUsers
            v1_users={data.v1_users}
            updateData={(updatedUser) => updateData('v1_users', updatedUser)}
          />
        )}
        {visibleComponent === 'adminTickets' && (
          <AdminTicketPage />
        )}
      </div>
    </div>
  );
};

// Styles for the component
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'laft',
    justifyContent: 'left',
    width: '100%',
    height: '100vh',
    backgroundColor: '#f4f4f4', // Light background for the page
    padding: '20px',
  },
  heading: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#333', // Dark text color for heading
    marginBottom: '20px',
  },
  subHeading: {
    fontSize: '1.2rem',
    color: '#666', // Lighter color for subheading
    marginBottom: '30px',
  },
  buttonContainer: {
    display: 'flex',
    gap: '15px', // Space between buttons
    marginBottom: '30px',
  },
  button: {
    padding: '12px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#007bff', // Blue background color
    color: 'white',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  },
  buttonHover: {
    backgroundColor: '#0056b3', // Darker blue on hover
  },
  componentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '1200px', // To limit width on larger screens
    margin: '0 auto',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', // Light shadow for depth
  },
};

export default Dashadmin;
